<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Icon } from '@components'
import { sortedDays, toRGB } from '@utils'
import moment from 'moment'

import { useAppointments } from '@store/appointments.js'
const $appointments = useAppointments()

import { useSettings } from '@store/settings'
const $settings = useSettings()

import { useCustomizer } from '@store/customizer'
const $customizer = useCustomizer()

// emit
const emit = defineEmits(['update:modelValue', 'change'])

// props
const props = defineProps({
    futureDays: {
        type: Number,
        default: 90
    }
})

const activeDate = ref({})

// original days array according to [new Date()]
const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',]

const activeCalender = ref({
    month: 1, // specify the month (1 for January, 2 for February, etc.)
    year: 0, // specify the year
    dates: [],
})

const generateDates = () => {
    const firstDayOfMonth = moment(`${activeCalender.value.year}-${activeCalender.value.month}-01`, 'YYYY-MM-DD')
    const lastDayOfMonth = moment(firstDayOfMonth).endOf('month')

    const currentDate = moment(firstDayOfMonth)
    const startDayOfCalenderWeek = sortedDays()[0]
    const endDayOfCalenderWeek = sortedDays()[sortedDays().length - 1]

    activeCalender.value.dates = []

    // calculate the number of days to add at the beginning
    const daysToAddAtStart = (currentDate.day() + 7 - days.indexOf(startDayOfCalenderWeek)) % 7
    // add blank objects at the beginning
    let dateStartOfLastMonth = firstDayOfMonth.subtract(daysToAddAtStart, 'days')
    for (let i = 0; i < daysToAddAtStart; i++) {
        activeCalender.value.dates.push({
            day: dateStartOfLastMonth.format('dddd'),
            date: dateStartOfLastMonth.date(),
            month: dateStartOfLastMonth.format('MMMM'),
            year: dateStartOfLastMonth.format('YYYY'),
            fullDate: dateStartOfLastMonth.format('YYYY-MM-DD')
        })

        dateStartOfLastMonth.add(1, 'days')
    }

    while (currentDate.isSameOrBefore(lastDayOfMonth)) {
        activeCalender.value.dates.push({
            day: currentDate.format('dddd'),
            date: currentDate.date(),
            month: currentDate.format('MMMM'),
            year: currentDate.format('YYYY'),
            fullDate: currentDate.format('YYYY-MM-DD'),
        })

        currentDate.add(1, 'days')
    }

    // calculate the number of days to add at the end
    const daysToAddAtEnd = ((days.indexOf(endDayOfCalenderWeek) + 7 - currentDate.day()) % 7) + 1
    // add blank objects at the end
    if (daysToAddAtEnd > 6) return
    let dateEndOfLastMonth = lastDayOfMonth.add(1, 'days')
    for (let i = 0; i < daysToAddAtEnd; i++) {
        activeCalender.value.dates.push({
            day: dateEndOfLastMonth.format('dddd'),
            date: dateEndOfLastMonth.date(),
            month: dateEndOfLastMonth.format('MMMM'),
            year: dateEndOfLastMonth.format('YYYY'),
            fullDate: dateEndOfLastMonth.format('YYYY-MM-DD')
        })

        dateEndOfLastMonth.add(1, 'days')
    }
}


onMounted(() => {
    if (!$appointments.selectedDate) {
        // set the current month and year
        activeCalender.value.month = moment().format('MM')
        activeCalender.value.year = moment().format('YYYY')
    } else {
        // if date is selected then set the active date
        activeCalender.value.year = moment($appointments.selectedDate.fullDate, 'YYYY-MM-DD').format('YYYY')
        activeCalender.value.month = moment($appointments.selectedDate.fullDate, 'YYYY-MM-DD').format('MM')
        activeDate.value = $appointments.selectedDate
    }
})

const divideArrayIntoGroups = (array, groupSize) => {
    const resultArray = []
    for (let i = 0; i < array.length; i += groupSize) {
        resultArray.push(array.slice(i, i + groupSize))
    }
    return resultArray
}

const getDates = computed(() => {
    generateDates()
    return divideArrayIntoGroups(activeCalender.value.dates, 7)
})

const isFuture = (fullDate) => {
    const startDate = moment().format('YYYY-MM-DD')
    const endDate = moment(fullDate, 'YYYY-MM-DD')

    const differenceInDays = endDate.diff(startDate, 'days')

    if (differenceInDays > props.futureDays) {
        return true
    } else {
        return false
    }
}

const changeMonth = (option) => {
    if (!option) return

    // on prev if month is current month then return
    if (option == 'prev' && moment().month(activeCalender.value.month - 1).format('MMMM') === moment().format('MMMM')) return
    // on next if month is future then return
    if (option == 'next' && isFuture(moment(`${activeCalender.value.year}-${activeCalender.value.month - 1}`, 'YYYY-MM').format('YYYY-MM-DD'))) return

    const currentMonth = moment({
        year: activeCalender.value.year,
        month: activeCalender.value.month - 1, // Moment.js months are 0-based
    })

    // get the details of next or prev month
    const changeMonthTo = option == 'next' ? currentMonth.clone().add(1, 'month') : currentMonth.clone().subtract(1, 'month')

    activeCalender.value.month = changeMonthTo.month() + 1
    activeCalender.value.year = changeMonthTo.year()
    activeCalender.value.dates = []

    generateDates()
}

const selectDate = (item) => {
    if (!item) return
    if (isDisabled(item)) return
    if (item.fullDate == "") return

    activeDate.value = item

    emit('update:modelValue', activeDate.value)
    emit('change', activeDate.value)

    // $appointments.setAppointment('booking_date', $appointments.appointment.booking_date)
}

const isActive = (item) => {
    if (moment(item.month, 'MMMM').isSame(moment(activeCalender.value.month, 'MM'), 'month') == false) return
    if (!item) return
    return item.fullDate == activeDate.value.fullDate
}

const isDisabled = (item) => {
    // Check if the date is in availableDates.
    const availableDates = $appointments.availableDates
    // console.log(availableDates.length > 0)
    if (moment(item.month, 'MMMM').isSame(moment(activeCalender.value.month, 'MM'), 'month') == false) return true
    if (moment(item.fullDate, 'YYYY-MM-DD').isBefore(moment(), 'day')) return true
    if (availableDates.length > 0) {
        const findDate = availableDates.find(date => moment(item.fullDate, 'YYYY-MM-DD').isSame(moment(date.date, 'YYYY-MM-DD'), 'day'))
        if (!findDate) return true
    }
}

const progressHeight = (item) => {
    // bail, if progress is not enabled
    if (!$settings.customizer.date.show_progress) return

    let findDate = $appointments.availableDates.find(date => item.fullDate == date.date)

    if (findDate) return Math.round((findDate.booked / findDate.total) * 100)
    else return 0
}

watch(() => $appointments.selectedDate, () => {
    if ($appointments.selectedDate) {
        activeDate.value = $appointments.selectedDate
    }
})
</script>

<template>
    <div class="calendar">
        <!-- calendar header -->
        <div class="calendar__header">
            <div class="calendar__month">{{ moment(`${activeCalender.year}-${activeCalender.month}-01`, 'YYYY-MM').format('MMMM YYYY') || 'N/A' }}</div>
            <div class="calendar__controls" :class="{ 'calendar__controls--rtl': $settings.isRTL }">
                <Icon 
                    @click="changeMonth('prev')" 
                    name="chevronLeft" 
                    size="20px"
                    class="calendar__controls-button"
                    :class="{ 'calendar__controls-button--disabled': moment().month(activeCalender.month - 1).format('MMMM') === moment().format('MMMM') }" />
                <Icon 
                    @click="changeMonth('next')" 
                    name="chevronRight" 
                    size="20px"
                    class="calendar__controls-button"
                    :class="{ 'calendar__controls-button--disabled': isFuture(moment(`${activeCalender.year}-${activeCalender.month - 1}`, 'YYYY-MM').format('YYYY-MM-DD')) }" />
            </div>
        </div>

        <!-- divider -->
        <div class="calendar__divider"></div>

        <!-- calendar days -->
        <div class="calendar__days">
            <div v-for="day in sortedDays()" 
                 :key="day" 
                 class="calendar__days-item">{{ day.slice(0, 3) }}</div>
        </div>

        <!-- calendar dates -->
        <div class="calendar__dates">
            <div v-for="(row, index) in getDates" 
                 :key="index" 
                 class="calendar__dates-row">
                <div v-for="item in row" 
                     :key="item"
                     @click="selectDate(item)"
                     class="calendar__dates-cell"
                     :class="{
                         'calendar__dates-cell--disabled': isDisabled(item),
                         'calendar__dates-cell--active': isActive(item),
                         'calendar__dates-cell--empty': item.fullDate == ''
                     }"
                     :style="`
                         background-color: ${isActive(item) ? `${$settings.customizer.design.brand_color} !important` : ''};
                         border: 1px solid ${isActive(item) ? `${$settings.customizer.design.brand_color}` : ''} !important;
                         border-color: ${isDisabled(item) ? 'transparent' : `${toRGB($settings.customizer.design.brand_color, 0.3)}`} !important;
                         border-radius: ${$customizer.isRounded ? '4px' : '0px'} !important;
                     `">
                    <div v-if="!isActive(item)" 
                         class="calendar__dates-cell-progress"
                         :style="`
                             height:${progressHeight(item)}%;
                             background-color: ${toRGB($settings.customizer.design.brand_color, 0.3)} !important;
                         `"></div>
                    <div class="calendar__dates-cell-text">{{ item.date }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped></style>